// ============================================================
// WORK GALLERY
// Category slugs are the slugified Google Drive folder names.
// Real local media is auto-detected at build time from /public/work/<slug>/
// by scripts/build-gallery.js (galleryManifest.json). Where a category has
// curated YouTube videos instead (YOUTUBE_MAP below), those take priority.
// Categories with no media of any kind are omitted entirely — their filter
// tab does not render.
// ============================================================
import manifest from "@/data/galleryManifest.json";

// All 15 categories (source of truth for names/slugs).
const ALL_CATEGORIES = [
    { slug: "ai-animation-based-product-video", name: "AI Animation based product video" },
    { slug: "ai-based-founder-shoot-video", name: "AI Based Founder Shoot Video" },
    { slug: "ai-based-landscape-video", name: "AI Based Landscape Video" },
    { slug: "ai-based-long-videos", name: "AI Based Long Videos" },
    { slug: "ai-detailing-product-based-videos", name: "AI Detailing Product Based Videos" },
    { slug: "ai-product-commercial-ad", name: "AI Product Commercial AD" },
    { slug: "ai-product-precision-videos", name: "AI Product Precision Videos" },
    { slug: "ai-stock-media-based", name: "AI Stock Media based" },
    { slug: "ai-ugc-brolls-based", name: "AI-UGC Brolls based" },
    { slug: "avatar-talking-video", name: "Avatar Talking Video" },
    { slug: "hook-based-ai-video", name: "Hook Based AI Video" },
    { slug: "human-stock-based", name: "Human Stock based" },
    { slug: "self-shot-based", name: "Self Shot based" },
    { slug: "studio-styled-ad-video-lamborghini-india", name: "Studio Styled AD Video Lamborghini India" },
    { slug: "video-snippets-based", name: "Video Snippets based" },
    { slug: "ai-cinematic-music-videos", name: "AI Cinematic & Music Videos" },
];

// Curated YouTube uploads (unlisted), by category slug. Extracted video IDs
// from the links supplied 22 Jul 2026.
const YOUTUBE_MAP = {
    "ai-animation-based-product-video": ["7PoFW-7PdQc"],
    "ai-based-founder-shoot-video": ["KV6sOgugaHw"],
    "ai-based-landscape-video": ["B0x9jwsdjxQ"],
    "ai-based-long-videos": ["XXCO00iwgJc", "e79fTad1eUU", "Zj30fuFBqqs"],
    "ai-detailing-product-based-videos": ["1t7rhDQszpQ"],
    "ai-product-commercial-ad": ["LvZ7lW-K9vI", "rdBzxeHjqr4", "RnkUXwd6XKY", "4-V514-e3Hs", "rOafK1nYXko"],
    "ai-product-precision-videos": ["GBoamQRIY6o", "NAsDDB61HxQ"],
    "ai-ugc-brolls-based": ["RdKeMtDB4f8"],
    "ai-cinematic-music-videos": ["i92-s583s2w", "ChfqFPMalQ4"],
};

function youtubeItems(cat) {
    return YOUTUBE_MAP[cat.slug].map((youtubeId, i) => {
        const n = String(i + 1).padStart(2, "0");
        return {
            id: `${cat.slug}-yt-${n}`,
            category: cat.slug,
            categoryName: cat.name,
            caption: cat.name,
            alt: `${cat.name} — AI visual created by Nikhil Joshi`,
            type: "youtube",
            youtubeId,
            // hqdefault is guaranteed to exist for every upload (incl. Shorts);
            // maxresdefault is not, so we don't risk a broken thumbnail.
            poster: `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`,
        };
    });
}

const PLACEHOLDER_SLOTS = 2;

function placeholderItems(cat) {
    return Array.from({ length: PLACEHOLDER_SLOTS }).map((_, i) => {
        const n = String(i + 1).padStart(2, "0");
        return {
            id: `${cat.slug}-${n}`,
            category: cat.slug,
            categoryName: cat.name,
            caption: cat.name,
            alt: `${cat.name} — AI visual created by Nikhil Joshi`,
            poster: `/work/${cat.slug}/clip-${n}.webp`,
            src: `/work/${cat.slug}/clip-${n}.mp4`,
            placeholder: true,
        };
    });
}

// A category is included only if it has YouTube videos and/or local media.
// The 7 categories with neither (AI Stock Media based, Avatar Talking Video,
// Hook Based AI Video, Human Stock based, Self Shot based, Studio Styled AD
// Video Lamborghini India, Video Snippets based) are omitted — no filter tab.
export const CATEGORIES = ALL_CATEGORIES.filter((cat) => {
    const hasYoutube = Boolean(YOUTUBE_MAP[cat.slug]?.length);
    const hasLocal = Boolean((manifest && manifest[cat.slug])?.length);
    return hasYoutube || hasLocal;
});

export const GALLERY = CATEGORIES.flatMap((cat) => {
    if (YOUTUBE_MAP[cat.slug]?.length) return youtubeItems(cat);

    const real = (manifest && manifest[cat.slug]) || [];
    if (real.length) {
        return real.map((item) => ({
            ...item,
            category: cat.slug,
            categoryName: cat.name,
            alt: item.alt || `${cat.name} — AI visual created by Nikhil Joshi`,
            caption: item.caption || cat.name,
        }));
    }
    return placeholderItems(cat);
});
